import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, ArrowDownToLine, ArrowUpFromLine, ClipboardCheck, MapPin } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { IssuesAlert, QtyStepper, StockBadge } from "@/components/common";
import { KitNeeds } from "@/components/MovementForm";
import { api, apiError, apiIssues, fmtQty } from "@/lib/api";
import { scanFeedback } from "@/lib/feedback";

const ART_TYPES = [
  { id: "entree", label: "Entrée", icon: ArrowDownToLine },
  { id: "sortie", label: "Sortie", icon: ArrowUpFromLine },
  { id: "ajustement", label: "Ajustement", icon: ClipboardCheck },
];

export function QuickMovementDialog({ target, onClose, onDone }) {
  const isKit = target?.kind === "kit";
  const item = target?.item;
  const [type, setType] = useState("sortie");
  const [qty, setQty] = useState(1);
  const [note, setNote] = useState("");
  const [issues, setIssues] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!target) return;
    setType(isKit ? "sortie_kit" : "sortie");
    setQty(1); setNote(""); setIssues([]);
  }, [target, isKit]);

  const pickType = (t) => { setType(t); setIssues([]); setQty(t === "ajustement" ? item.stock : 1); };
  const n = Number(qty) || 0;
  const after = !isKit && item ? (type === "entree" ? item.stock + n : type === "sortie" ? item.stock - n : n) : null;

  const submit = async () => {
    setBusy(true); setIssues([]);
    try {
      const { data } = await api.post("/movements", { type, code: item.code, quantity: n, note: note || null });
      scanFeedback(true, "Mouvement validé");
      toast.success(data.applied[0]?.replace(/^1\. /, ""));
      onDone?.();
      onClose();
    } catch (e) {
      scanFeedback(false, "Mouvement bloqué");
      const list = apiIssues(e);
      setIssues(list.length ? list : [apiError(e)]);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="quick-movement-dialog" onOpenAutoFocus={(e) => e.preventDefault()}>
        {item && <>
          <DialogHeader>
            <DialogTitle className="font-display text-2xl uppercase" data-testid="quick-movement-title">{isKit ? `Sortie du kit ${item.code}` : `${item.code} — ${item.designation}`}</DialogTitle>
            <DialogDescription className="flex flex-wrap items-center gap-3">
              {isKit ? <span>{item.designation} · <b data-testid="quick-kit-buildable">{item.buildable}</b> réalisable(s), limitant <span className="font-mono">{item.limiting}</span></span> : <>
                <span>Stock : <b className="font-mono text-slate-900" data-testid="quick-current-stock">{fmtQty(item.stock)}</b> {item.unite}</span>
                <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{item.emplacement || "—"}</span>
                <StockBadge stock={item.stock} seuil={item.seuil_min} />
              </>}
            </DialogDescription>
          </DialogHeader>
          {!isKit && (
            <div className="grid grid-cols-3 gap-2">
              {ART_TYPES.map(({ id, label, icon: Icon }) => (
                <button key={id} type="button" onClick={() => pickType(id)} data-testid={`quick-type-${id}`}
                  className={`flex h-16 flex-col items-center justify-center gap-1 rounded-md border-2 text-sm font-semibold transition-colors ${type === id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                  <Icon className="h-5 w-5" />{label}
                </button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">{type === "ajustement" ? "Quantité comptée" : isKit ? "Nombre de kits" : "Quantité"}</div>
              <QtyStepper value={qty} onChange={setQty} min={type === "ajustement" ? 0 : 1} testid="quick-qty" />
            </div>
            {!isKit && <div className={`rounded px-4 py-2 text-right ${after < 0 ? "bg-rose-50 text-rose-700" : "bg-slate-50"}`}><div className="text-xs uppercase text-slate-500">Après</div><div className="font-mono text-2xl font-bold" data-testid="quick-after-stock">{fmtQty(after)}</div></div>}
          </div>
          {isKit && <KitNeeds kit={item} qty={qty} />}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optionnelle)" className="h-12" data-testid="quick-note-input" />
          <IssuesAlert title="Mouvement bloqué" issues={issues} testid="quick-movement-issues" />
          <DialogFooter className="gap-2">
            <Button variant="outline" className="h-12" onClick={onClose} data-testid="quick-cancel-button">Annuler</Button>
            <Button className="h-12 min-w-[160px] gap-2" disabled={busy || n < (type === "ajustement" ? 0 : 1)} onClick={submit} data-testid="quick-submit-button"><CheckCircle2 className="h-4 w-4" /> Valider</Button>
          </DialogFooter>
        </>}
      </DialogContent>
    </Dialog>
  );
}
